import { addCallback, removeCallback } from '../../lib/teact/teactn';

import type { SharedState } from '../types';
import type { ClientBoundMessageEvent } from './sharedState.worker';

import { APP_NAME } from '../../config';
import { deepDiff, type DiffObject } from '../../util/deepDiff';
import { deepMerge } from '../../util/deepMerge';
import { throttle } from '../../util/schedulers';
import { INITIAL_SHARED_STATE } from '../initialState';
import { getGlobal, setGlobal } from '..';

interface GetFullStateEvent {
  type: 'reqGetFullState';
  localState: SharedState;
}

interface UpdateStateEvent {
  type: 'reqUpdateState';
  update: DiffObject<SharedState>;
}

export type WorkerBoundMessageEvent = GetFullStateEvent | UpdateStateEvent;

const SHARED_STATE_SYNC_THROTTLE_MS = 500;

let sharedWorker: SharedWorker | undefined;

// Tracks if the client has been synced with the shared state.
let synced = false;
let lastSharedState: SharedState = INITIAL_SHARED_STATE;
// Guard: true while applying an incoming update from SharedWorker, to prevent echo-back
let isApplyingRemoteUpdate = false;

export function initSharedState(localState: SharedState) {
  sharedWorker = new SharedWorker(new URL('./sharedState.worker.ts', import.meta.url), { name: APP_NAME });

  sharedWorker.port.addEventListener('message', onMessage);
  sharedWorker.port.start();

  sendToWorker({ type: 'reqGetFullState', localState });

  addCallback(onGlobalChange);
}

function onGlobalChange() {
  if (isApplyingRemoteUpdate) return;

  const global = getGlobal();
  if (global.sharedState === lastSharedState) return;
  if (global.sharedState.isInitial) return;

  updateSharedState(global.sharedState);
}

function onMessage(event: MessageEvent<ClientBoundMessageEvent>) {
  const data = event.data;
  isApplyingRemoteUpdate = true;
  try {
    switch (data.type) {
      case 'stateUpdate': {
        const state = deepMerge(lastSharedState, data.update);
        updateGlobal(state);
        break;
      }
      case 'fullState':
        synced = true;
        updateGlobal(data.state);
        break;
    }
  } finally {
    isApplyingRemoteUpdate = false;
  }
}

export function destroySharedStatePort() {
  if (!sharedWorker) return;
  sharedWorker.port.removeEventListener('message', onMessage);
  sharedWorker.port.close();
  sharedWorker = undefined;
  removeCallback(onGlobalChange);
}

function updateSharedState(update: SharedState) {
  if (!synced) return;
  const diff = deepDiff(lastSharedState, update);
  lastSharedState = update;
  if (typeof diff === 'symbol') return;

  sendToWorker({ type: 'reqUpdateState', update: diff });
}

let pendingEvent: WorkerBoundMessageEvent | undefined;

const flushToWorker = throttle(() => {
  if (!sharedWorker || !pendingEvent) return;

  sharedWorker.port.postMessage(pendingEvent);
  pendingEvent = undefined;
}, SHARED_STATE_SYNC_THROTTLE_MS, true);

function sendToWorker(event: WorkerBoundMessageEvent) {
  if (!sharedWorker) {
    throw new Error('Shared worker not initialized');
  }

  // For initial full-state request, send immediately
  if (event.type === 'reqGetFullState') {
    sharedWorker.port.postMessage(event);
    return;
  }

  // For updates, throttle — only keep the latest pending event
  pendingEvent = event;
  flushToWorker();
}

function updateGlobal(update: Partial<SharedState>) {
  let global = getGlobal();
  lastSharedState = {
    ...global.sharedState,
    ...update,
  };

  global = {
    ...global,
    sharedState: lastSharedState,
  };
  setGlobal(global);
}
